<template>
<section class="delivery-info">
  <div class="city-info">
    <div v-if="settings.display_map" class="city-map-container">
      <yandex-map v-if="mapPropsAreReady" v-bind="mapProps"></yandex-map>
    </div>
    <table v-if="settings.display_stocks" class="city-stocks-table">
      <thead>
        <tr>
          <th>Адрес</th>
          <th>Телефон</th>
          <th>Режим работы</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="stock in transformedStocks">
          <td class="city-stock-address">
            <div class="city-stock-info-container">
              <div class="city-stock-info-icon">
                <span class="rounded-icon">
                  <i class="fas fa-map-marker-alt"></i>
                </span>
              </div>
              <div class="city-stock-info-text">
                {{ stock.address }}
              </div>
            </div>
          </td>
          <td class="city-stock-phone">
            <div class="city-stock-info-container">
              <div class="city-stock-info-icon">
                <span class="rounded-icon">
                  <i class="fas fa-phone fa-flip-horizontal"></i>
                </span>
              </div>
              <div class="city-stock-info-text">
                {{ stock.phone }}
              </div>
            </div>
          </td>
          <td class="city-stock-schedule">
            <div class="city-stock-info-container">
              <div class="city-stock-info-icon">
                <span class="rounded-icon">
                  <i class="fas fa-clock"></i>
                </span>
              </div>
              <div class="city-stock-info-text">
                {{ stock.schedule }}
                <small class="subtitle">
                  сегодня
                  /
                  <span class="text-primary-dark">
                    {{ stock.closingAtOrNextOpenAt }}
                  </span>
                </small>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  <div class="delivery-important-info">
    <div class="definitions-list">
      <definition v-for="definition in definitions"
                  :key="definition.key"
                  :title="definition.title"
      >
        <div v-html="definition.content"></div>
      </definition>
    </div>
  </div>
</section>
</template>

<script>
import Definition from '@/components/lists/Definition'
import YandexMap from '@/components/maps/YandexMap'
import { mapGetters } from 'vuex'
import HasDefinitions from '@/mixins/HasDefinitions'

export default {
  components: {
    Definition,
    YandexMap,
  },

  mixins: [
    HasDefinitions,
  ],

  computed: {
    ...mapGetters({
      activeCity: 'store/activeCity',
      activeCityStocks: 'store/activeCityStocks',
      settings: 'settings/deliveryPaymentsDelivery',
    }),

    visibleStocks() {
      return this.activeCityStocks.filter(s => !s.hidden)
    },

    transformedStocks() {
      return this.visibleStocks.map(stock => {
        return {
          name: stock.display_name,
          address: stock.address.full,
          phone: this.stockPhone(stock),
          schedule: this.stockSchedule(stock),
          closingAtOrNextOpenAt: this.getClosingAtOrNextOpenAt(stock),
        }
      })
    },

    mapPropsAreReady() {
      return !!this.activeCity && this.activeCity.id
    },

    mapProps() {
      return {
        center: [
          this.activeCity.lat,
          this.activeCity.lng,
        ],

        placemarks: this.visibleStocks
          .map(stock => {
            if (!stock.address) {
              return null
            }

            let text = `<strong>${stock.display_name}</strong><br><address>${stock.address.full}</address>`
            const phone = this.stockPhone(stock)

            if (phone) {
              text += `<br>Телефон: <strong>${phone}</strong>`
            }

            if (stock.schedule && stock.schedule.today) {
              if (stock.schedule.today.open) {
                text += `<br>Режим работы: ${stock.schedule.today.hours[0]}`
              } else {
                text += `<br>Откроется ${stock.schedule.today.next_open_at.short}`
              }
            }

            return {
              coords: [
                stock.address.lat,
                stock.address.lng,
              ],
              text,
            }
          })
          .filter(p => !!p),

        zones: Array.isArray(this.activeCity.delivery_zones) ? this.getDeliveryZones() : [],
      }
    },
  },

  methods: {
    stockPhone(stock) {
      if (!stock || !stock.contacts || !stock.contacts.length) {
        return ''
      }

      const index = stock.contacts.findIndex(c => c.type.id === 'phone')

      if (index === -1) {
        return ''
      }

      return stock.contacts[index].value.national
    },

    stockSchedule(stock) {
      if (!stock || !stock.schedule || !stock.schedule.today) {
        return ''
      }

      return stock.schedule.today.hours[0]
    },

    getClosingAtOrNextOpenAt(stock) {
      if (!stock || !stock.schedule || !stock.schedule.today) {
        return ''
      }

      if (!stock.schedule.today.open && stock.schedule.today.next_open_at && stock.schedule.today.next_open_at.short) {
        return `откроется ${stock.schedule.today.next_open_at.short}`
      } else if (stock.schedule.today.open && stock.schedule.today.closing_at && stock.schedule.today.closing_at.diff) {
        return `закроется ${stock.schedule.today.closing_at.diff.toLowerCase()}`
      }

      return ''
    },

    getDeliveryZones() {
      if (!this.settings.display_zones) {
        return []
      }

      return this.activeCity.delivery_zones.map(zone => {
        let html = ''

        const metaDataMargin = this.zoneHasMetaData(zone) ? ' mb-2' : ''

        if (zone.name && zone.description) {
          html += `<h3 class="text-lg font-bold mb-2">${zone.name}</h3>`
          html += `<p class="text-xs${metaDataMargin}">${zone.description}</p>`
        } else if (zone.name) {
          html += `<h3 class="text-lg font-bold${metaDataMargin}">${zone.name}</h3>`
        } else if (zone.description) {
          html += `<p class="text-xs${metaDataMargin}">${zone.description}</p>`
        }

        if (zone.min_price !== null) {
          html += `<p class="text-xs">Минимальная сумма заказа: ${zone.min_price.full}.</p>`
        }

        if (zone.free_delivery_price !== null) {
          html += `<p class="text-xs">Бесплатная доставка при заказе от ${zone.free_delivery_price.full}.</p>`
        }

        if (zone.delivery_price !== null) {
          html += `<p class="text-xs">Стоимость платной доставки: ${zone.delivery_price.full}.</p>`
        }

        if (zone.delivery_time) {
          html += `<p class="text-xs">Среднее время доставки: ${zone.delivery_time}</p>`
        }

        return {
          id: zone.id,
          coordinates: zone.coordinates,
          color: zone.color,
          html: html ? `<div class="p-2">${html}</div>` : '',
        }
      })
    },

    zoneHasMetaData(zone) {
      return zone.min_price !== null || zone.free_delivery_price !== null
          || zone.delivery_price !== null || zone.delivery_time !== null
    },
  },

  data() {
    return {
      //
    }
  }
}
</script>

<style lang="less">
@import "~@/assets/less/components/icons";

.delivery-info {
  .city-info {
    @apply border-4 border-secondary border-solid p-4;

    .city-map-container {
      .city-map {
        @apply shadow-lg;
      }
    }

    .city-stocks-table {
      @apply hidden w-full;

      thead tr th,
      tbody tr td {
        @apply w-1/3 px-4 py-4;
      }

      thead {
        tr {
          th {
            @apply font-bold text-left text-sm;
          }
        }
      }

      tbody {
        tr {
          @apply border-t border-grey-light border-solid;

          td {
            .city-stock-info-container {
              @apply flex w-full;

              .city-stock-info-icon {
                @apply w-1/6;
              }

              .city-stock-info-text {
                @apply w-5/6;

                small {
                  @apply block;
                }
              }
            }
          }
        }
      }
    }
  }
}

@screen lg {
  .delivery-info {
    .city-info {
      .city-stocks-table {
        @apply table;
      }
    }
  }
}
</style>
